/*
  mezuzi, theme 169.0 (#105 step 2) — keep keyboard focus on the same control after the cart re-renders.

  Every cart update (a quantity typed, + / -, remove) re-renders the cart items (Tinker's morphSection after
  POST /cart/change); the control the customer was on is removed and focus falls to <body> (review-169/
  READING-105.md: ~1.5 s after the change, every case). With the cart a native modal (#75) the next Tab
  then starts from the browser. This puts focus back where it was: the same control of the same LINE
  (by the line's key, data-key - not its index), or, when that line is gone (quantity 0 / removed), the
  next line's quantity field, else the cart's close button. Named an accessibility addition (the source not
  measured - its store answers our session carts with a bot check).

  Only when focus fell to <body> from a cart control (focusout with no new target): the control MOVED by
  the morph (still on the page) gets it back; one REMOVED -> by its line as above; one that just became DISABLED (- at the line's minimum -> the line's quantity
  field) - a click elsewhere or a deliberate blur is left alone. Ours, no Tinker file.
*/

const ROOT = '#cart-drawer, cart-items-component';
const ROW = '[data-key]';
const CLOSE = '#cart-drawer .theme-drawer__close-button';

/** @param {Element} element @returns {string} a selector for the same control inside a line */
function kindOf(element) {
  if (element.matches('input')) return 'input[name="updates[]"], cart-quantity-selector-component input';
  for (const name of ['quantity-minus', 'quantity-plus', 'cart-items__remove', 'cart-items__title', 'cart-items__media-container']) {
    if (element.classList.contains(name)) return `.${name}`;
  }
  return element.tagName.toLowerCase();
}

/** The last pointer press - a click on empty space inside the cart blurs on purpose; that is left alone. */
let lastPointer = -Infinity;
document.addEventListener('pointerdown', () => (lastPointer = performance.now()), true);

/** @param {FocusEvent} event */
function onFocusOut(event) {
  const element = event.target;
  if (event.relatedTarget || !(element instanceof HTMLElement) || !element.closest(ROOT)) return;
  if (performance.now() - lastPointer < 400) return;
  const row = element.closest(ROW);
  const key = row instanceof HTMLElement ? row.dataset.key : null;
  const rows = [...document.querySelectorAll(`cart-items-component ${ROW}`)];
  const index = row ? rows.indexOf(row) : -1;
  const kind = kindOf(element);

  // The re-render removes the old node after this event: look once the frame has settled.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement) return;
      if (!document.querySelector('#cart-drawer dialog[open]') && !element.closest('main')) return; // the drawer closed meanwhile
      // Still on the page: the morph MOVED it (detached and re-attached - measured, dbg105b: the same node, connected, focus lost) ->
      // the same element again; or it just became disabled (- at the minimum) -> its line's quantity field.
      if (element.isConnected) {
        const target = element.matches(':disabled') ? element.closest(ROW)?.querySelector('cart-quantity-selector-component input') : element;
        if (target instanceof HTMLElement && target.closest(ROOT)) target.focus({ preventScroll: true });
        return;
      }

      /** @type {HTMLElement | null} */
      let target = null;
      if (key) {
        const same = [...document.querySelectorAll(`cart-items-component ${ROW}`)].find(
          (candidate) => candidate instanceof HTMLElement && candidate.dataset.key === key
        );
        target = same ? same.querySelector(kind) : null;
        if (!target && !same) {
          const now = [...document.querySelectorAll(`cart-items-component ${ROW}`)];
          const next = now[Math.min(Math.max(index, 0), now.length - 1)];
          target = next ? next.querySelector('cart-quantity-selector-component input') : null;
        }
      }
      target = target || document.querySelector(CLOSE);
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    })
  );
}

document.addEventListener('focusout', onFocusOut, true);
